import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// Add MUI imports for legacy UI
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Fab from '@mui/material/Fab';
import Zoom from '@mui/material/Zoom';
import CircularProgress from '@mui/material/CircularProgress';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import RestartIcon from '@mui/icons-material/RestartAlt';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * ChatBot component matching legacy GOATChat UI.
 * Props:
 * - apiUrl: string – endpoint to POST chat messages
 * - analyticsCallback: function(eventName, payload) – called for chat_opened, chat_closed, chat_question
 * - className: string – optional CSS class for styling
 * - initialMessage: string – initial bot message
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function ChatBot(_ref) {
  let {
    systemPrompt,
    // userPrompt,
    apiUrl = '/.netlify/functions/chat',
    analyticsCallback,
    initialMessage,
    chatTitle,
    chatAvatar,
    options = {},
    chatName
  } = _ref;
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: initialMessage
  }]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const outerContainerRef = useRef(null);
  const inputContainerRef = useRef(null);
  console.log(chatAvatar, options);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);
  useEffect(() => {
    const handleWheel = e => {
      e.preventDefault();
      e.stopPropagation();
    };
    const inputContainer = inputContainerRef.current;
    if (inputContainer) {
      inputContainer.addEventListener('wheel', handleWheel, {
        passive: false
      });
    }
    return () => {
      if (inputContainer) {
        inputContainer.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const track = (event, data) => {
    if (typeof analyticsCallback === 'function') {
      analyticsCallback(event, data);
    }
  };
  const handleSend = async () => {
    if (!input.trim() || loading) return;

    // console.log('ChatBot: Sending message:', input)
    const userMsg = {
      role: 'user',
      content: input
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    track('chat_question', {
      content_type: 'chat',
      question: input,
      chat_name: chatName
    });
    try {
      // console.log('ChatBot: Making API call to:', apiUrl)
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userPrompt: input,
          history: messages,
          systemPrompt
        })
      });

      // console.log('ChatBot: API response status:', res.status)
      const data = await res.json();
      // console.log('ChatBot: Received response:', data)

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply
      }]);
      // eslint-disable-next-line no-unused-vars
    } catch (err) {
      // console.error('ChatBot: API call failed:', err)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I am having trouble connecting. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };
  const resetConversation = () => {
    setMessages([{
      role: 'assistant',
      content: initialMessage
    }]);
    setInput('');
  };
  const handleClose = () => {
    setIsOpen(false);
    resetConversation();
    track('chat_closed', {
      content_type: 'chat',
      chat_name: 'G.O.A.T. Race Assistant'
    });
  };
  const handleToggle = () => {
    if (isOpen) {
      handleClose();
    } else {
      setIsOpen(true);
      track('chat_opened', {
        content_type: 'chat',
        chat_name: 'G.O.A.T. Race Assistant'
      });
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Fab, {
      color: "primary",
      onClick: handleToggle,
      sx: {
        position: 'fixed',
        bottom: 24,
        right: 24,
        bgcolor: '#378d84',
        '&:hover': {
          bgcolor: '#2c716a'
        }
      },
      children: isOpen ? /*#__PURE__*/_jsx(CloseIcon, {}) : /*#__PURE__*/_jsx(ForumOutlinedIcon, {
        fontSize: "large"
      })
    }), /*#__PURE__*/_jsx(Zoom, {
      in: isOpen,
      children: /*#__PURE__*/_jsx("div", {
        ref: outerContainerRef,
        style: {
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 999
        },
        children: /*#__PURE__*/_jsx("div", {
          style: {
            position: 'absolute',
            bottom: 90,
            right: 24,
            pointerEvents: 'auto'
          },
          children: /*#__PURE__*/_jsxs(Paper, {
            sx: {
              border: 'solid 1px #444',
              width: {
                xs: '94vw',
                sm: 420
              },
              height: 560,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
              zIndex: 1000,
              overscrollBehavior: 'contain'
            },
            children: [/*#__PURE__*/_jsxs(Box, {
              sx: {
                p: 1,
                bgcolor: '#553561',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              },
              children: [/*#__PURE__*/_jsx(Typography, {
                variant: "subtitle2",
                fontWeight: "bold",
                children: chatTitle
              }), /*#__PURE__*/_jsx(IconButton, {
                onClick: resetConversation,
                size: "small",
                sx: {
                  color: 'white'
                },
                children: /*#__PURE__*/_jsx(RestartIcon, {
                  fontSize: "small"
                })
              })]
            }), /*#__PURE__*/_jsxs(Box, {
              ref: scrollRef,
              sx: {
                flexGrow: 1,
                p: 2,
                overflowY: 'auto',
                bgcolor: '#222',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': {
                  width: '6px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'black',
                  borderRadius: '3px'
                }
              },
              children: [messages.map((m, i) => /*#__PURE__*/_jsx(Box, {
                sx: {
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2
                },
                children: /*#__PURE__*/_jsx(Paper, {
                  sx: {
                    p: 1,
                    maxWidth: '90%',
                    borderRadius: 4,
                    bgcolor: m.role === 'user' ? '#378d84' : '#444',
                    color: m.role === 'user' ? 'white' : 'text.primary',
                    boxShadow: 1,
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    userSelect: 'text'
                  },
                  children: /*#__PURE__*/_jsx(ReactMarkdown, {
                    remarkPlugins: [remarkGfm],
                    components: {
                      p: _ref2 => {
                        let {
                          children
                        } = _ref2;
                        return /*#__PURE__*/_jsx(Typography, {
                          variant: "body2",
                          sx: {
                            m: 0,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          },
                          children: children
                        });
                      },
                      strong: _ref3 => {
                        let {
                          children
                        } = _ref3;
                        return /*#__PURE__*/_jsx(Typography, {
                          variant: "caption",
                          component: "span",
                          sx: {
                            fontWeight: 'bold',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          },
                          children: children
                        });
                      },
                      li: _ref4 => {
                        let {
                          children
                        } = _ref4;
                        return /*#__PURE__*/_jsx(Typography, {
                          variant: "",
                          component: "li",
                          sx: {
                            m: 0,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          },
                          children: children
                        });
                      },
                      th: _ref5 => {
                        let {
                          children
                        } = _ref5;
                        return /*#__PURE__*/_jsx(Typography, {
                          variant: "caption",
                          component: "th",
                          sx: {
                            fontWeight: 'bold',
                            p: 1,
                            border: '1px solid #ddd',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          },
                          children: children
                        });
                      },
                      td: _ref6 => {
                        let {
                          children
                        } = _ref6;
                        return /*#__PURE__*/_jsx(Typography, {
                          variant: "body2",
                          component: "td",
                          sx: {
                            p: 1,
                            border: '1px solid #ddd',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          },
                          children: children
                        });
                      },
                      a: _ref7 => {
                        let {
                          ...props
                        } = _ref7;
                        return /*#__PURE__*/_jsx(Typography, {
                          component: "a",
                          sx: {
                            color: 'white',
                            textDecoration: 'underline'
                          },
                          ...props
                        });
                      }
                    },
                    children: m.content
                  })
                })
              }, i)), loading && /*#__PURE__*/_jsx(CircularProgress, {
                size: 20,
                sx: {
                  ml: 1,
                  color: '#444'
                }
              })]
            }), /*#__PURE__*/_jsxs(Box, {
              ref: inputContainerRef,
              sx: {
                p: 2,
                borderTop: '1px solid #eee',
                display: 'flex',
                gap: 1
              },
              children: [/*#__PURE__*/_jsx(TextField, {
                fullWidth: true,
                size: "small",
                placeholder: "Ask a question...",
                value: input,
                onChange: e => setInput(e.target.value),
                onKeyDown: e => e.key === 'Enter' && handleSend(),
                autoComplete: "off",
                slotProps: {
                  input: {
                    autoComplete: 'off'
                  }
                }
              }), /*#__PURE__*/_jsx(IconButton, {
                onClick: handleSend,
                disabled: loading,
                children: /*#__PURE__*/_jsx(SendIcon, {})
              })]
            })]
          })
        })
      })
    })]
  });
}
ChatBot.propTypes = {
  apiUrl: PropTypes.string,
  distance: PropTypes.number,
  analyticsCallback: PropTypes.func,
  initialMessage: PropTypes.string
};